import React from 'react';
import { Grid } from '@mui/material';
import DrillDownProductInfo from './ProductInfo';
import DrillDownEvolutionChart from './EvolutionChart';
import DrillDownCfopChart from './CfopChart';
import DrillDownDetailedTransactions from './DetailedTransactions';
import DrillDownSummaryTable from './SummaryTable';
import DrillDownPartnersSection from './PartnersSection';
import DrillDownProductsSection from './ProductsSection';
import { formatMonthYear } from './formatters';
import { NCM_DRILL_DOWN_TITLES } from './constants';

// Adaptador para dados de NCM usando a interface do drill down de produto
interface NcmDrillDownData {
  ncm?: {
    ncm: string;
    descricao_ncm: string;
    cod_item: string;
    total_credito_icms: number;
    total_debito_icms: number;
    total_transacoes?: number;
    total_produtos: number;
    quantidade_total: number;
    quantidade_entrada: number;
    quantidade_saida: number;
    preco_medio_unitario: number;
    valor_total_item_compra: number;
    valor_total_item_venda: number;
  };
  transacoes_por_mes?: Array<{
    mes: number;
    ano: number;
    credito_icms: number;
    debito_icms: number;
    total_transacoes?: number;
    total_transacoes_credito?: number;
    total_transacoes_debito?: number;
    produtos_distintos: number;
    quantidade_total: number;
    quantidade_entrada: number;
    quantidade_saida: number;
    valor_total_item: number;
  }>;
  top_cfops?: Array<{
    cfop: string;
    total_icms: number;
    total_transacoes: number;
    produtos_distintos: number;
  }>;
  principais_produtos?: Array<{
    cod_item: string;
    descricao_produto: string;
    credito_icms: number;
    debito_icms: number;
    total_icms: number;
    total_transacoes: number;
    quantidade_total: number;
    valor_total_item?: number;
    valor_total_compra?: number;
    valor_total_venda?: number;
  }>;
  parceiros_comerciais?: {
    fornecedores: Array<{
      cod_participante: string;
      nome: string;
      uf: string;
      municipio: string;
      total_compras: number;
      icms_compras: number;
      quantidade_comprada: number;
      total_documentos: number;
      total_operacoes: number;
      primeira_transacao: string;
      ultima_transacao: string;
      preco_medio: number;
      ticket_medio: number;
      total_competencias: number;
      competencias_ativas: string;
    }>;
    clientes: Array<{
      cod_participante: string;
      nome: string;
      uf: string;
      municipio: string;
      total_vendas: number;
      icms_vendas: number;
      quantidade_vendida: number;
      total_documentos: number;
      total_operacoes: number;
      primeira_transacao: string;
      ultima_transacao: string;
      preco_medio: number;
      ticket_medio: number;
      total_competencias: number;
      competencias_ativas: string;
    }>;
  };
  transacoes_detalhadas?: Array<{
    cfop: string;
    dt_emissao: string;
    num_doc: string;
    vl_icms: number;
    qtd: number;
    vl_item: number;
    ind_oper: string;
    cod_item: string;
    descr_compl: string;
  }>;
}

interface NcmDrillDownProps {
  data: NcmDrillDownData | null;
}

const NcmDrillDown: React.FC<NcmDrillDownProps> = ({ data }) => {
  if (!data || !data.ncm) return null;

  // Adaptando dados de NCM para o formato esperado pelos componentes
  const adaptedProductData = {
    descr_compl: `NCM ${data.ncm.ncm} - ${data.ncm.descricao_ncm}`,
    cod_item: data.ncm.cod_item,
    total_debito_icms: data.ncm.total_debito_icms,
    total_credito_icms: data.ncm.total_credito_icms,
    quantidade_entrada: data.ncm.quantidade_entrada,
    quantidade_saida: data.ncm.quantidade_saida,
    quantidade_total: data.ncm.quantidade_total,
    total_transacoes: data.ncm.total_transacoes,
    valor_total_item_compra: data.ncm.valor_total_item_compra,
    valor_total_item_venda: data.ncm.valor_total_item_venda
  };

  // Gerar lineData para o gráfico de evolução
  const lineData = [
    {
      id: 'Débitos ICMS',
      data: (data.transacoes_por_mes || []).map(item => {
        // Calcular total de transações de débito usando novos campos ou fallback
        const transacoesDebito = item.total_transacoes_debito !== undefined 
          ? item.total_transacoes_debito 
          : item.total_transacoes || 0;
        
        return {
          x: formatMonthYear(item.mes, item.ano),
          y: item.debito_icms || 0,
          transacoes: item.total_transacoes_debito
        };
      })
    },
    {
      id: 'Créditos ICMS',
      data: (data.transacoes_por_mes || []).map(item => {
        // Calcular total de transações de crédito usando novos campos ou fallback
        const transacoesCredito = item.total_transacoes_credito !== undefined 
          ? item.total_transacoes_credito 
          : item.total_transacoes || 0;
        
        return {
          x: formatMonthYear(item.mes, item.ano),
          y: item.credito_icms || 0,
          transacoes: item.total_transacoes_credito
        };
      })
    }
  ];

  return (
    <Grid container spacing={3}>
      <DrillDownProductInfo produto={adaptedProductData} />
      
      <DrillDownEvolutionChart 
        lineData={lineData}
        title="Evolução Mensal do NCM"
      />
      
      <DrillDownCfopChart 
        top_cfops={data.top_cfops || []} 
        title={NCM_DRILL_DOWN_TITLES.ANALISE_CFOP}
      />
      
      <DrillDownProductsSection
        produtos={data.principais_produtos || []}
        title={NCM_DRILL_DOWN_TITLES.PRINCIPAIS_PRODUTOS}
        showPagination={true}
        maxHeight="500px"
      />
      
      <DrillDownPartnersSection 
        parceiros_comerciais={data.parceiros_comerciais || { fornecedores: [], clientes: [] }}
        title={NCM_DRILL_DOWN_TITLES.PARCEIROS_COMERCIAIS}
      />
      
      <DrillDownDetailedTransactions 
        transacoes_detalhadas={data.transacoes_detalhadas || []}
        title={NCM_DRILL_DOWN_TITLES.TRANSACOES_DETALHADAS}
      />
    </Grid>
  );
};

export default NcmDrillDown; 